import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deletePaymentMethod,
  getPaymentMethodsList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  PaymentMethod,
  updatePaymentMethodBunch,
} from "store/payments/methods/slice";
import { loyaltyListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import MethodsFilter from "./MethodsFilter";
import {
  NotificationTypes,
  showNotification,
} from "../../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const PaymentMethodsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;
  const canRead = permissions?.read ?? false;
  const activeMethodId = searchParams.get("entityId");

  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
    id: string;
    transactionType: string;
    providerTitle: string;
  }>({
    title: "none",
    isActive: "none",
    id: "none",
    transactionType: "none",
    providerTitle: "none",
  });
  const [currentLimit, setLimit] = useState<number>(loyaltyListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [provider, setProvider] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);
  const [type, setType] = useState<{ value: number | string; label: string }[]>(
    [],
  );

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store["payment-methods"].list);
  const isListFetched = useAppSelector(
    (store) => store["payment-methods"].isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store["payment-methods"].totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store["payment-methods"].totalPages,
  );
  const activePage = useAppSelector(
    (store) => store["payment-methods"].activePage,
  );
  const selectedItems = useAppSelector(
    (store) => store["payment-methods"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store["payment-methods"].processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'payment-methods',
    isListFetched
  });

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getPaymentMethodsList({
          limit: currentLimit,
          offset: activePage - 1,
          title: search,
          providerTitle: provider,
          transactionType: (type[0]?.value as string) ?? "",
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    if (!canUpdate) return;
    try {
      await dispatch(
        updatePaymentMethodBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.paymentMethod}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.paymentMethod }));
  };
  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.paymentMethod}`);
    dispatch(openModal({ activeModal: ModalTypes.paymentMethod }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;
    const result = await dispatch(deletePaymentMethod(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <MethodsFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleSearch={setSearch}
        providerValue={provider}
        handleProvider={setProvider}
        selectedActive={active}
        handleUpdateActive={setActive}
        selectedType={type}
        handleUpdateType={setType}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {canCreate && (
              <ActionBtn
                txt={t("addMethod")}
                handleClick={handleOpenCreateModal}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          {canUpdate && <div className="page-list-header--box">
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !canUpdate}
            />
          </div>}
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={15} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 && (
              <EmptyState message={t("noPaymentMethodsFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={
                  list as Array<
                    PaymentMethod & { id: number; providerImage: string }
                  >
                }
                order={order}
                idField="id"
                selectedItems={selectedItems}
                hasCheckbox={canUpdate}
                hasEdit={canUpdate}
                hasView={canRead && !canUpdate}
                hasDelete={canDelete}
                hasOptions={canRead || canUpdate || canDelete}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onDelete={(id) => handleDeleteItem(id)}
                onEdit={canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                onView={canRead && !canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                activeRowId={activeMethodId}
                processingId={processingItemId}
                columns={{
                  sortingColumns: {
                    isActive: {
                      value: t("active"),
                    },
                    transactionType: {
                      value: t("type"),
                      hasSorting: true,
                    },
                    providerTitle: {
                      value: t("provider"),
                      hasSorting: true,
                    },
                  },
                  hiddenColumns: [
                    "providerId",
                    "providerImage",
                    "isTest",
                    "soon",
                  ],
                }}
                customColumns={(row) => ({
                  title: (
                    <div className="payment-method-title-cell">
                      <span>{row.title}</span>
                      {row.isTest && (
                        <span className="badge badge--test">test</span>
                      )}
                      {row.soon === true && (
                        <span className="badge badge--soon">
                          <span className="badge--soon__label">soon</span>
                        </span>
                      )}
                    </div>
                  ),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={loyaltyListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsList;
